import { useCallback } from 'react';
import { issueMessage } from '../../lib/rcu/catalog/issue';
import { SEND_TRANSPORT } from '../../lib/rcu/catalog/link';
import type { Result } from '../../lib/rcu/result';
import type { RcuDocument } from '../../lib/rcu/wire';
import { useRcuConfigStore } from '../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../stores/rcuLinkStore';
import type { LinkStatus } from '../../stores/rcuLinkStore';
import { useToastStore } from '../../stores/toastStore';

export interface RcuLinkApi {
  readonly link: LinkStatus;
  /** Commands waiting for a publish or a POST to finish. */
  readonly inFlight: number;
  /**
   * Sends a built command and reports what happened. A builder's refusal is
   * shown as an error and never sent, so panels can pass results straight in.
   */
  send: (result: Result<RcuDocument>, what: string) => Promise<boolean>;
}

/**
 * Sending, as the panels need it: one call, one honest toast.
 *
 * Over MQTT the toast says the command is on its way and the reply will appear in
 * the feed, because that is all a publish proves. Over HTTP it says the command
 * was delivered — the firmware answers, but a browser cannot read the answer.
 */
export const useRcuLink = (): RcuLinkApi => {
  const config = useRcuConfigStore((state) => state.config);
  const link = useRcuLinkStore((state) => state.link);
  const inFlight = useRcuLinkStore((state) => state.inFlight);
  const send = useRcuLinkStore((state) => state.send);
  const success = useToastStore((state) => state.success);
  const error = useToastStore((state) => state.error);
  const info = useToastStore((state) => state.info);

  const run = useCallback(
    async (result: Result<RcuDocument>, what: string): Promise<boolean> => {
      if (!result.ok) {
        error(issueMessage(result.issue));
        return false;
      }

      const outcome = await send(config, result.value);
      if (!outcome.ok) {
        error(`${what}: ${outcome.detail}`);
        return false;
      }
      if (outcome.transport === SEND_TRANSPORT.HTTP) {
        info(`${what} delivered over HTTP; the RCU does not confirm to a browser.`);
        return true;
      }
      success(`${what} sent; the reply appears in the feed.`);
      return true;
    },
    [config, send, success, error, info],
  );

  return { link, inFlight, send: run };
};

export default useRcuLink;
